import React, { useState, useEffect } from "react";
import "./ProductHome.css";

const ProductHome = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeSection, setActiveSection] = useState("about");

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["about", "projects", "experience", "education", "skills", "contact"];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Kolkata",
    });
  };

  const projects = [
    {
      year: "2024",
      title: "Increasing team productivity by 40%",
      subtitle: "Content Cursor - Social Media Management SaaS",
      description:
        "Owned product roadmap and led development of a collaborative workflow tool for content creators. Conducted user research to prioritize features and implemented analytics dashboard for data-driven iterations.",
      metrics: ["+40% productivity", "User research", "Analytics dashboard"],
      link: "https://contentcursor.com",
      tags: ["Product Owner", "React", "Node.js", "TypeScript"],
    },
    {
      year: "2024",
      title: "AI-Powered Web Intelligence",
      subtitle: "Chatpilot - Intelligent Web Scraping Chatbot",
      description:
        "Built an AI-powered chatbot that intelligently scrapes and analyzes web content, enabling users to have conversations with any webpage.",
      metrics: ["AI Integration", "Web Scraping", "NLP"],
      link: "https://github.com/leocodeio-chatpilot/chatpilot",
      tags: ["AI/ML", "Python", "React"],
    },
    {
      year: "2024",
      title: "Simplifying URL Management",
      subtitle: "Domain-In-Domain - URL/Domain Management Platform",
      description:
        "Created a platform to manage and organize URLs and domains efficiently, improving workflow for developers managing multiple projects.",
      metrics: ["URL Management", "Developer Tool"],
      link: "https://did.leocode.tech",
      tags: ["Full-Stack", "TypeScript", "PostgreSQL"],
    },
  ];

  const experiences = [
    {
      company: "Reelongo",
      role: "Technical Product Intern",
      period: "Dec 2025 - Feb 2026",
      location: "Hyderabad - Hybrid",
      highlights: [
        "Bridged product strategy and engineering by designing 3 high-traffic web applications",
        "Led end-to-end product development of 2 cross-platform mobile apps from concept to public release",
        "Translated user feedback and stakeholder requirements into actionable product features",
        "Architected scalable REST APIs supporting real-time content delivery",
      ],
    },
    {
      company: "Netlabs Australia Pvt Ltd",
      role: "Software Engineer",
      period: "Jul 2024 - Aug 2025",
      location: "Murarrie, Queensland - Remote",
      highlights: [
        "Developed scalable full-stack applications for enterprise clients using Agile/Scrum",
        "Improved mobile user engagement metrics by 25% through cross-platform solutions",
        "Collaborated with product managers to prioritize features and deliver sprint commitments",
        "Identified and resolved complex technical bottlenecks, optimizing system throughput",
      ],
    },
  ];

  const skills = {
    "Product Strategy & Roadmapping": [
      "Product Roadmap",
      "User Research",
      "A/B Testing",
      "Stakeholder Management",
      "Agile/Scrum",
      "JIRA",
    ],
    "Design & Research Methods": [
      "Figma",
      "User Interviews",
      "Wireframing",
      "Prototyping",
      "Competitive Analysis",
    ],
    "Data & Analytics": [
      "SQL",
      "Data-Driven Decision Making",
      "Metrics Definition",
      "Dashboard Design",
    ],
    "Technical Foundation": [
      "JavaScript/TypeScript",
      "React/Next.js",
      "Node.js",
      "REST APIs",
      "MongoDB/PostgreSQL",
    ],
  };

  const education = [
    {
      degree: "MTech in Product Design and Management (PDM)",
      school: "International Institute of Information Technology Hyderabad",
      period: "Aug 2025 - Present",
    },
    {
      degree: "BTech in Computer Science and Engineering (Data Science)",
      school: "Anil Neerukonda Institute of Technology and Sciences",
      period: "Aug 2021 - May 2025",
    },
  ];

  const testimonials = [
    {
      quote:
        "Harsha has an exceptional ability to translate complex technical constraints into clear product decisions. He bridges the gap between engineering and business seamlessly.",
      author: "Engineering Collaborator",
      role: "Senior Developer",
    },
    {
      quote:
        "His structured thinking and data-driven approach to product development consistently delivers measurable outcomes. A true product mindset.",
      author: "Product Team Lead",
      role: "Startup Mentor",
    },
  ];

  return (
    <div className="product-container">
      {/* Sidebar */}
      <aside className="product-sidebar">
        <div className="sidebar-content">
          <div className="sidebar-header">
            <h1 className="sidebar-name">Harsha Vardhan</h1>
            <p className="sidebar-title">Aspiring Product Manager</p>
          </div>

          <div className="sidebar-meta">
            <div className="meta-item">
              <span className="meta-label">Local Time</span>
              <span className="meta-value">{formatTime(currentTime)} IST</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Location</span>
              <span className="meta-value">Hyderabad, India</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Status</span>
              <span className="meta-value status-available">
                <span className="status-dot"></span>
                Open to Opportunities
              </span>
            </div>
          </div>

          <nav className="sidebar-nav">
            {["about", "projects", "experience", "education", "skills", "contact"].map(
              (section) => (
                <button
                  key={section}
                  className={`nav-item ${activeSection === section ? "active" : ""}`}
                  onClick={() => scrollToSection(section)}
                >
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </button>
              )
            )}
          </nav>

          <div className="sidebar-links">
            <a
              href="mailto:saiharsha9897@gmail.com"
              className="sidebar-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Email
            </a>
            <a
              href="https://linkedin.com/in/sai-harsha-vardhan-pittada1"
              className="sidebar-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              LinkedIn
            </a>
            <a
              href="https://github.com/leocodeio"
              className="sidebar-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              GitHub
            </a>
          </div>

        </div>
      </aside>

      {/* Main Content */}
      <main className="product-main">
        {/* Hero Section */}
        <section id="about" className="section hero-section">
          <h2 className="hero-statement">
            Turning User Insights into
            <span className="accent"> Scalable Products</span>
          </h2>
          <p className="hero-description">
            Aspiring Product Manager with a strong technical foundation and
            hands-on experience owning product roadmaps from concept to launch.
            I combine deep understanding of engineering constraints with
            business acumen to deliver high-impact products.
          </p>
          <div className="hero-highlights">
            <div className="highlight-item">
              <span className="highlight-number">GATE 2024</span>
              <span className="highlight-label">AIR 1243 in DS & AI</span>
            </div>
            <div className="highlight-item">
              <span className="highlight-number">MTech</span>
              <span className="highlight-label">PDM @ IIIT Hyderabad</span>
            </div>
            <div className="highlight-item">
              <span className="highlight-number">2+</span>
              <span className="highlight-label">Years Building Products</span>
            </div>
          </div>
        </section>

        <hr className="section-divider" />

        {/* Projects Section */}
        <section id="projects" className="section">
          <h3 className="section-title">Selected Projects</h3>
          <div className="projects-list">
            {projects.map((project, index) => (
              <article key={index} className="project-item">
                <div className="project-header">
                  <span className="project-year">{project.year}</span>
                  <h4 className="project-title">{project.title}</h4>
                </div>
                <p className="project-subtitle">{project.subtitle}</p>
                <p className="project-description">{project.description}</p>
                <div className="project-metrics">
                  {project.metrics.map((metric, i) => (
                    <span key={i} className="metric-tag">
                      {metric}
                    </span>
                  ))}
                </div>
                <div className="project-footer">
                  <div className="project-tags">
                    {project.tags.map((tag, i) => (
                      <span key={i} className="tech-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <a
                    href={project.link}
                    className="project-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Project
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>

        <hr className="section-divider" />

        {/* Experience Section */}
        <section id="experience" className="section">
          <h3 className="section-title">Experience</h3>
          <div className="experience-list">
            {experiences.map((exp, index) => (
              <article key={index} className="experience-item">
                <div className="experience-header">
                  <div>
                    <h4 className="experience-company">{exp.company}</h4>
                    <p className="experience-role">{exp.role}</p>
                  </div>
                  <div className="experience-meta">
                    <span className="experience-period">{exp.period}</span>
                    <span className="experience-location">{exp.location}</span>
                  </div>
                </div>
                <ul className="experience-highlights">
                  {exp.highlights.map((highlight, i) => (
                    <li key={i}>{highlight}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <hr className="section-divider" />

        {/* Education Section */}
        <section id="education" className="section">
          <h3 className="section-title">Education</h3>
          <div className="education-list">
            {education.map((edu, index) => (
              <div key={index} className="education-item">
                <div className="education-header">
                  <span className="education-degree">{edu.degree}</span>
                  <span className="education-period">{edu.period}</span>
                </div>
                <span className="education-school">{edu.school}</span>
              </div>
            ))}
          </div>
        </section>

        <hr className="section-divider" />

        {/* Skills Section */}
        <section id="skills" className="section">
          <h3 className="section-title">Skills & Arsenal</h3>
          <div className="skills-grid">
            {Object.entries(skills).map(([category, items]) => (
              <div key={category} className="skill-category">
                <h4 className="skill-category-title">{category}</h4>
                <ul className="skill-list">
                  {items.map((skill, i) => (
                    <li key={i} className="skill-item">
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <hr className="section-divider" />

        {/* Testimonials Section */}
        <section className="section">
          <h3 className="section-title">What People Say</h3>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <blockquote key={index} className="testimonial-item">
                <p className="testimonial-quote">"{testimonial.quote}"</p>
                <footer className="testimonial-author">
                  <span className="author-name">{testimonial.author}</span>
                  <span className="author-role">{testimonial.role}</span>
                </footer>
              </blockquote>
            ))}
          </div>
        </section>

        <hr className="section-divider" />

        {/* Contact Section */}
        <section id="contact" className="section contact-section">
          <h3 className="section-title">Let's Connect</h3>
          <p className="contact-description">
            I'm always interested in discussing product challenges, startup
            ideas, or opportunities to collaborate on building impactful
            products.
          </p>
          <div className="contact-links">
            <a
              href="mailto:saiharsha9897@gmail.com"
              className="contact-link primary"
            >
              saiharsha9897@gmail.com
            </a>
            <div className="contact-social">
              <a
                href="https://linkedin.com/in/sai-harsha-vardhan-pittada1"
                className="social-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
              <a
                href="https://github.com/leocodeio"
                className="social-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                GitHub
              </a>
              <a
                href="tel:+919603028848"
                className="social-link"
              >
                +91 9603028848
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="product-footer">
          <p>
            Built with focus on clarity and impact.
          </p>
        </footer>
      </main>
    </div>
  );
};

export default ProductHome;
